import React, { useContext, useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Environment } from 'src/Environment';
import { Field, FieldArray, Form, Formik, FormikErrors } from 'formik';
import { isValidID } from 'src/helpers/validation';
import {
  asylumRegex,
  passportRegex,
  refugeeRegex,
  stripWhitespace,
  toTitleCase,
} from 'src/helpers/formatting';
import TealiumEvents from 'src/helpers/tealium-events';
import MiniAppEvents from 'src/helpers/miniapp-events';
import {
  AcquiringChannelFlags,
  BizWalletName,
  CustomerTypes,
  SalesforceContext,
  Steps,
} from 'src/context/salesforce-context';
import {
  Checkbox,
  Info,
  MutableAccordion,
  MutableList,
  ShowIf,
  TextWithIconLayout,
} from 'src/components';
import { MutableListItem } from 'src/components/mutable-list/mutable-list';
import { MutableAccordionItem } from 'src/components/mutable-accordion/mutable-accordion';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { getDeviceIdByName, getTotalDevices } from 'src/helpers/devices';
import { BUSINESS_TYPE_KEY_TO_NAME_LOOKUP } from 'src/helpers/constants';
import { CONSUMER_TYPE } from 'src/services/salesforce.service';
import BizWalletProModal from './biz-wallet-pro-modal/biz-wallet-pro-modal';
import useIsMobileDisplaySize from 'src/hooks/use-is-mobile-display-size';
import { DirectorInfoFields } from './director-info-fields/director-info-fields';
import { isValidName } from 'src/helpers/validation';
import {
  IdentityTypeDirectors,
  isUnderAge,
  isUserUnderAgeIdNumber,
  isValidPassportExpiryDate,
} from 'src/features/common';
import UnderageModal from '../underage-modal/underage-modal';
import { FormStatus, getDirectorsFormStatus } from 'src/features/onboarding/helpers/fields-status';
import { useKillApplication } from 'src/hooks/use-kill-application';
import {
  DirectorIdTypes,
  IdentityTypeItem,
} from 'src/features/onboarding/pages/director-details/director-details-page';
import { checkAcquiringChannel } from 'src/helpers/devices';
import './your-business-ids.scss';

type Director = {
  identityType?: IdentityTypeItem;
  title?: string;
  initials?: string;
  firstName?: string;
  surname?: string;
  idNumber?: string;
  passportNumber?: string;
  dateOfBirth?: string;
  gender?: string;
  passportExpiryDate?: string;
  countryOfOrigin?: string;
};

type FormState = {
  idNumbers: string[];
  directors: Director[];
  creditCheckConsent: boolean;
};

export default function YourBusinessIds(props) {
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const isMobile = useIsMobileDisplaySize();

  const {
    merchant,
    setMerchant,
    lead,
    documents,
    setDocumentsWithDeletionHandling,
    customerType,
    setDeviceOrder,
    uniqueTerminals,
    isVodapayChannel,
  } = useContext(SalesforceContext);

  const isPendingApp = useMemo(() => !!merchant.isPendingApp, [merchant.isPendingApp]);

  const isRented = checkAcquiringChannel(
    AcquiringChannelFlags.rentOnlineFicaOnline,
    merchant?.opportunity?.deliveries,
  );

  const isCreditVettingEnabled = !Environment.current?.disabledFeatures?.creditVetting && isRented;
  const isPassportEnabled = !Environment.current?.disabledFeatures?.passport;
  const [isUserUnderAge, setUserIsUnderAge] = useState(false);
  const { setApplicationKilled } = useKillApplication(Steps.YOUR_BUSINESS, Steps.BUSINESS_IDS);

  const [textTable, setTextTable] = useState(
    getTextTable(
      CustomerTypes.normal,
      CONSUMER_TYPE.owner,
      BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.partnership,
    ),
  );

  const [showBizWalletProModal, setShowBizWalletProModal] = useState(false);

  useEffect(() => {
    if (merchant?.account?.business_type) {
      const assistantId = lead.dealerId || lead.agentId || '';
      TealiumEvents.yourDirectorsPageLoadFormStartCombined(
        merchant.account.business_type,
        customerType,
        assistantId,
        merchant.isPendingApp,
      );
    }
  }, [merchant?.account?.business_type]);

  function isAgentWording(customerType, consumerType) {
    switch (customerType) {
      case CustomerTypes.dealer:
      case CustomerTypes.agent:
        return true;
      case CustomerTypes.normal:
        return consumerType !== CONSUMER_TYPE.owner;
      case CustomerTypes.pos:
        return false;
    }
  }

  function getDirectorTitle(businessType): string {
    const memberNamesSentenceCase = {
      DIRECTOR: 'Director',
      MEMBER: 'Member',
      TRUSTEE: 'Trustee',
      PARTNER: 'Partner',
    };

    switch (businessType) {
      case BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.company:
      case BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.non_profit_org:
        return memberNamesSentenceCase.DIRECTOR;
      case BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.close_corp:
        return memberNamesSentenceCase.MEMBER;
      case BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.trust:
        return memberNamesSentenceCase.TRUSTEE;
      default:
        // BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.partnership:
        return memberNamesSentenceCase.PARTNER;
    }
  }

  function getTextTable(customerType, consumerType, businessType) {
    const directorTitle = getDirectorTitle(businessType);
    const idInputFieldPlaceHolderText = `${directorTitle} ID`;
    const passportInputFieldPlaceHolderText = `${directorTitle} passport number`;
    const subtitleOwner =
      'Some confidential but necessary details to complete your application, we will keep it safe.';
    const subtitleAgent =
      "Some confidential but necessary details to complete your customer's application, we will keep it safe.";
    const addButtonText = `Add a ${directorTitle.toLowerCase()}`;

    if (isAgentWording(customerType, consumerType)) {
      return {
        pageTitle: `Tell us about your customer's ${directorTitle.toLowerCase()}s`,
        pageSubtitle: subtitleAgent,
        idInputFieldPlaceHolderText: idInputFieldPlaceHolderText,
        addButtonText: addButtonText,
        directorTitle: directorTitle,
        passportInputFieldPlaceHolderText: passportInputFieldPlaceHolderText,
      };
    } else {
      return {
        pageTitle: `Tell us about your ${directorTitle.toLowerCase()}s`,
        pageSubtitle: subtitleOwner,
        idInputFieldPlaceHolderText: idInputFieldPlaceHolderText,
        addButtonText: addButtonText,
        directorTitle: directorTitle,
        passportInputFieldPlaceHolderText: passportInputFieldPlaceHolderText,
      };
    }
  }

  useEffect(() => {
    if (customerType && lead.consumerType && lead.typeOfBusiness) {
      const textTableNew = getTextTable(lead.customerType, lead.consumerType, lead.typeOfBusiness);
      setTextTable(textTableNew);
    }
  }, [lead]);

  useEffect(() => {
    props.toggleNext(isFormValid(getFormValues()));
  }, [merchant]);

  const creditCheckTooltipText =
    'A credit check helps us verify your bank account details to make sure that the ' +
    'registered account not only exists but also belongs to you so we can successfully credit or debit it.';
  const creditCheckRadioButtonText =
    'All directors consent to the processing of my personal information, including credit bureau checks.';

  const getFormValues = (): FormState => {
    return {
      directors:
        merchant?.account?.directors?.length !== 0
          ? merchant.account.directors.map((director) => {
              const idType: IdentityTypeItem | undefined = DirectorIdTypes.find(
                (type) =>
                  type.value ===
                  (director.id_type !== 'passport'
                    ? director.id_type || IdentityTypeDirectors.RSA_ID
                    : toTitleCase(director.id_type)),
              );

              const directorValues: Director = {
                title: director.title,
                initials: director.initials,
                firstName: director.first_name,
                surname: director.surname,
                identityType: idType,
                idNumber: director.directors_id_number,
              };

              if (director.id_type !== IdentityTypeDirectors.RSA_ID) {
                directorValues.dateOfBirth = director.date_of_birth;
                directorValues.gender = director.gender;
                directorValues.passportExpiryDate = director.passport_expiry_date;
                directorValues.countryOfOrigin = director.country_of_origin;
              }

              return directorValues;
            })
          : [
              {
                identityType: DirectorIdTypes.find(
                  (type) => type.value === IdentityTypeDirectors.RSA_ID,
                ),
              },
            ],
      idNumbers: merchant?.account?.idNumbers || [''],
      creditCheckConsent: merchant?.account?.directors_credit_vetting_consent || false,
    };
  };

  const initialStatus = useMemo((): FormStatus | undefined => {
    if (!merchant.isPendingApp) {
      return undefined;
    }

    if (!merchant.pageUpdates) {
      throw new Error('Your business ids: isPending set but no page updates available');
    }

    const directors = getFormValues().directors;
    return getDirectorsFormStatus(merchant.pageUpdates, { directors }, true);
  }, [merchant.isPendingApp]);

  const setFormValues = (values: FormState) => {
    const merchantNew = {
      ...merchant,
      account: {
        ...merchant.account,
        directors: values.directors.map((director) => {
          const valuesToSave: { [key: string]: unknown } = {
            title: director.title,
            initials: director.initials,
            first_name: director.firstName,
            surname: director.surname,
            id_type: director.identityType?.value,
            directors_id_number: director.idNumber,
          };

          if (director.identityType?.value !== IdentityTypeDirectors.RSA_ID) {
            valuesToSave.date_of_birth = director.dateOfBirth;
            valuesToSave.gender = director.gender;
            valuesToSave.passport_expiry_date = director.passportExpiryDate;
            valuesToSave.country_of_origin = director.countryOfOrigin;
          }

          return valuesToSave;
        }),
        id_numbers: values.directors.map((director) => stripWhitespace(director.idNumber)),
      },
    };

    if (isCreditVettingEnabled) {
      merchantNew.account.directors_credit_vetting_consent = values.creditCheckConsent;
    }

    setMerchant(merchantNew);

    // extra side effect: delete any ID documents linked to remove id numbers
    if (!!documents?.documents) {
      const filteredDocuments = documents.documents.filter(
        (doc) =>
          !doc.id_number ||
          values.directors.map((director) => director.idNumber).includes(doc.id_number) ||
          doc.id_number === merchant.contact.id_number,
      );

      setDocumentsWithDeletionHandling({ documents: filteredDocuments });
    }
  };

  const onAddDirector = (directors, addDirector) => {
    if (directors.length === 0 || !merchant?.account?.sov_requested) {
      addDirector({
        identityType: DirectorIdTypes.find((type) => type.value === IdentityTypeDirectors.RSA_ID),
      });
      TealiumEvents.yourDirectorsAddDirector(
        directors.length + 1,
        customerType,
        merchant.isPending,
      );
    } else {
      setShowBizWalletProModal(true);
    }
  };

  const renderBizWalletProModal = (directors, addDirector) => {
    const handleModalRemoveSOV = () => {
      setDeviceOrder(getDeviceIdByName(BizWalletName, uniqueTerminals), 0, 0);
      setMerchant({
        ...merchant,
        account: {
          ...merchant.account,
          sov_requested: false,
        },
      });
    };

    const handleModalAddDirectorConfirm = () => {
      if (directors.length > 0) {
        const totalDevices = getTotalDevices(merchant.opportunity?.deliveries);
        addDirector({
          identityType: DirectorIdTypes.find((type) => type.value === IdentityTypeDirectors.RSA_ID),
        });
        TealiumEvents.yourDirectorsAddDirector(
          directors.length + 1,
          customerType,
          merchant.isPending,
        );
        setShowBizWalletProModal(false);
        handleModalRemoveSOV();
        if (totalDevices === 1) {
          props.quickNavigate(Steps.CHOOSE_A_SOLUTION);
        }
      }
    };

    const handleModalAddDirectorReject = () => {
      setShowBizWalletProModal(false);
    };

    const handleModalDone = () => {
      window.location.href = 'https://vodapay.vodacom.co.za/vodapay/business/home';
    };

    return (
      <BizWalletProModal
        show={showBizWalletProModal}
        totalDevices={getTotalDevices(merchant.opportunity?.deliveries)}
        isVodapayChannel={isVodapayChannel}
        onConfirmButtonClickAddDirector={handleModalAddDirectorConfirm}
        onDeclineButtonClickAddDirector={handleModalAddDirectorReject}
        onDoneButtonClickVodpayChannel={handleModalDone}
      />
    );
  };

  const renderCreditCheckConsentCheckbox = () => {
    const creditCheckConsentInputField = () => (
      <div className="consent-checkbox">
        <Field
          name="creditCheckConsent"
          type="checkbox"
          component={MiniAppFocusWrapper}
          innerComponent={Checkbox}
          checkboxText={() => (
            <TextWithIconLayout
              text={creditCheckRadioButtonText}
              renderIcon={() => (
                <Info
                  text={creditCheckTooltipText}
                  onOpenEvent={() => TealiumEvents.yourDirectorCreditConsentInfo(customerType)}
                  lightMode
                />
              )}
            />
          )}
          automationid="Onboarding_Create_Account_Your_Business_Ids_Field_Credit_Consent"
        />
      </div>
    );

    const element = document.getElementById('form-footer-content');

    return isMobile ? (
      creditCheckConsentInputField()
    ) : element ? (
      ReactDOM.createPortal(creditCheckConsentInputField(), element)
    ) : (
      <></>
    );
  };

  return (
    <div className="YourBusinessIDs" data-testid="YourBusinessIDs">
      <div className="outer-container">
        <Formik<FormState>
          initialValues={getFormValues()}
          initialStatus={initialStatus}
          validate={validateForm}
          validateOnChange={true}
          validateOnBlur={false}
          enableReinitialize={true}
          onSubmit={() => {}}
        >
          {({ values }) => {
            return (
              <Form>
                <UnderageModal
                  show={isUserUnderAge}
                  onClose={() => setUserIsUnderAge(false)}
                  onExitApplication={() => {
                    setApplicationKilled(true);
                  }}
                />
                <div className="has-text-centered">
                  <h1 className="page-title heading-four title-text">{textTable.pageTitle}</h1>
                  <p className="page-title content-normal page-subtitle subtext">
                    {textTable.pageSubtitle}
                  </p>
                </div>
                <div className="business-details-container">
                  <>
                    <div className="business-details">
                      {/* @ts-ignore */}
                      <FieldArray name="directors">
                        {({ push: addDirector, remove: removeDirectorAtIndex }) => {
                          const mutableListItems: MutableListItem[] = values.directors.map(
                            (director, index) => {
                              return {
                                remove: () => {
                                  removeDirectorAtIndex(index);
                                  TealiumEvents.yourDirectorsRemoveDirector(
                                    values.directors.length - 1,
                                    customerType,
                                  );
                                },
                                render: () => (
                                  <Field
                                    name={`directors[${index}]`}
                                    component={DirectorInfoFields}
                                    automationIdSuffix={index}
                                    idInputFieldPlaceHolderText={
                                      textTable.idInputFieldPlaceHolderText
                                    }
                                    passportInputFieldPlaceHolderText={
                                      textTable.passportInputFieldPlaceHolderText
                                    }
                                    isMiniProgram={inMiniProgram}
                                    isForeignNationalsEnabled={isPassportEnabled}
                                    isCreditVettingEnabled={isCreditVettingEnabled}
                                  />
                                ),
                                automationIdDescription: 'your business ids',
                              };
                            },
                          );

                          const getMutableAccordionItems = (): MutableAccordionItem[] =>
                            mutableListItems.map((listItem, index) => {
                              const director = values.directors[index];
                              let isDirectorPending = Object.values(
                                initialStatus?.initialDisabled.directors[index] || {},
                              ).every((disabled) => disabled);

                              return {
                                ...listItem,
                                title:
                                  director.title && director.firstName && director.surname
                                    ? `${director.title} ${director.firstName} ${director.surname}`
                                    : `${index + 1}. ${textTable.directorTitle} details`,
                                disabled: isPendingApp && isDirectorPending,
                              } as MutableAccordionItem;
                            });

                          const automationIdPrefix = 'Onboarding_Create_Account_Business_Ids';

                          return (
                            <>
                              {isCreditVettingEnabled || isPassportEnabled ? (
                                <MutableAccordion
                                  items={getMutableAccordionItems()}
                                  automationIdPrefix={automationIdPrefix}
                                />
                              ) : (
                                <MutableList
                                  items={mutableListItems}
                                  automationIdPrefix={automationIdPrefix}
                                />
                              )}

                              <ShowIf condition={!isPendingApp}>
                                <label className="add-id-button">
                                  <button
                                    className={`is-hidden`}
                                    onClick={() => onAddDirector(values.directors, addDirector)}
                                  />

                                  <span
                                    automationid={`${props.automationid}_Plus`}
                                    className={`is-icon-4 icon-plus reskin-button`}
                                  />
                                  <span className="tiny-sub-heading">
                                    {textTable.addButtonText}
                                  </span>
                                </label>

                                {renderBizWalletProModal(values.directors, addDirector)}
                              </ShowIf>
                            </>
                          );
                        }}
                      </FieldArray>

                      <ShowIf condition={isCreditVettingEnabled}>
                        {renderCreditCheckConsentCheckbox()}
                      </ShowIf>
                    </div>
                  </>
                </div>

                <div className="mini-spacer" />
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );

  function isFormValid(values) {
    const valuesClean = getCleanedValues(values);
    const errors = getErrors(valuesClean);
    return !isAnyErrors(errors);
  }

  function validateDirectorsCreditVettingEnabled(directors: Director[]) {
    const errorsAll: FormikErrors<Director>[] = [];

    const passportNumbers = new Set<string>();
    const idNumbers = new Set<string>();

    directors.forEach((director, index) => {
      const errors: FormikErrors<Director> = {};

      if (!director.title) {
        errors.title = 'Title is invalid';
      }

      if (!director.initials || !isValidName(director.initials)) {
        errors.initials = 'Initials is invalid';
      }

      if (!director.firstName || !isValidName(director.firstName)) {
        errors.firstName = 'First name is invalid';
      }

      if (!director.surname || !isValidName(director.surname)) {
        errors.surname = 'Surname is invalid';
      }

      switch (director.identityType?.value) {
        case IdentityTypeDirectors.PASSPORT:
          if (
            !director.passportNumber ||
            director.passportNumber.length === 0 ||
            passportNumbers.has(director.passportNumber)
          ) {
            errors.passportNumber = 'Passport number is invalid';
          } else {
            passportNumbers.add(director.passportNumber);
          }

          if (!director.dateOfBirth) {
            errors.dateOfBirth = 'Date of birth is invalid';
          }

          if (director.dateOfBirth && isUnderAge(new Date(director.dateOfBirth))) {
            errors.dateOfBirth = 'The age limit is 18 years or older';
            setUserIsUnderAge(true);
          }

          if (!director.gender) {
            errors.gender = 'Gender is invalid';
          }

          if (!director.passportExpiryDate) {
            errors.passportExpiryDate = 'Passport expiry date is invalid';
          }

          if (!director.countryOfOrigin) {
            errors.countryOfOrigin = 'Country of origin is invalid';
          }

          break;

        case IdentityTypeDirectors.RSA_ID:
          if (
            director.idNumber &&
            isValidID(director.idNumber) &&
            isUserUnderAgeIdNumber(director.idNumber)
          ) {
            setUserIsUnderAge(true);
            errors.idNumber = 'The age limit is 18 years or older';
          }
          if (
            !director.idNumber ||
            director.idNumber.length === 0 ||
            !isValidID(director.idNumber) ||
            idNumbers.has(director.idNumber)
          ) {
            errors.idNumber = 'ID number is invalid';
          } else {
            idNumbers.add(director.idNumber);
          }
          break;
      }

      if (Object.keys(errors).length > 0) {
        errorsAll[index] = errors;
      }
    });

    return errorsAll;
  }

  function validateDirectorCreditVettingDisabled(values: Director) {
    const errors: Director = {};

    if (values.idNumber && isValidID(values.idNumber) && isUserUnderAgeIdNumber(values.idNumber)) {
      setUserIsUnderAge(true);
      errors.idNumber = 'The age limit is 18 years or older';
    }

    if (!values.idNumber || values.idNumber.length === 0 || !isValidID(values.idNumber)) {
      errors.idNumber = 'ID number is invalid';
    }

    return errors;
  }

  function validateDirectorPassportEnabled(director: Director) {
    const errors: Director = {};

    if (director.identityType?.value === IdentityTypeDirectors.RSA_ID) {
      if (!director.idNumber || !isValidID(stripWhitespace(director.idNumber))) {
        errors.idNumber = 'ID number is invalid';
      }
    }

    if (
      director.identityType?.value === IdentityTypeDirectors.REFUGEE &&
      (!director.idNumber || !refugeeRegex.test(director.idNumber))
    ) {
      errors.idNumber = 'The Document ID is invalid';
    }

    if (
      director.identityType?.value === IdentityTypeDirectors.ASYLUM_SEEKER &&
      (!director.idNumber || !asylumRegex.test(director.idNumber))
    ) {
      errors.idNumber = 'The Document ID is invalid';
    }

    if (
      director.identityType?.value === IdentityTypeDirectors.REFUGEE ||
      director.identityType?.value === IdentityTypeDirectors.ASYLUM_SEEKER
    ) {
      if (!director.dateOfBirth) {
        errors.dateOfBirth = 'Date of birth is invalid';
      }

      if (director.dateOfBirth && isUnderAge(new Date(director.dateOfBirth))) {
        errors.dateOfBirth = 'The age limit is 18 years or older';
        setUserIsUnderAge(true);
      }
    }

    if (
      director.identityType?.value === IdentityTypeDirectors.PASSPORT &&
      (!director.idNumber || !passportRegex.test(director.idNumber))
    ) {
      errors.idNumber = 'Passport number is invalid';
    }

    if (director.identityType?.value !== IdentityTypeDirectors.RSA_ID) {
      if (!director.countryOfOrigin) {
        errors.countryOfOrigin = 'Country of origin is invalid';
      }

      if (!director.passportExpiryDate) {
        errors.passportExpiryDate = `${
          director.identityType?.value === IdentityTypeDirectors.PASSPORT ? 'Passport' : 'Document'
        } expiry date is invalid`;
      }

      if (
        director.passportExpiryDate &&
        !isValidPassportExpiryDate(new Date(director.passportExpiryDate))
      ) {
        errors.passportExpiryDate = `Your ${
          director.identityType?.value === IdentityTypeDirectors.PASSPORT ? 'passport' : 'document'
        } must be valid for a minimum of 6 months.`;
      }

      if (!director.dateOfBirth) {
        errors.dateOfBirth = 'Date of birth is invalid';
      }

      if (director.dateOfBirth && isUnderAge(new Date(director.dateOfBirth))) {
        errors.dateOfBirth = 'The age limit is 18 years or older';
        setUserIsUnderAge(true);
      }
    }

    if (director.identityType?.value !== IdentityTypeDirectors.RSA_ID || isCreditVettingEnabled) {
      if (!director.firstName) {
        errors.firstName = 'Please enter your first name.';
      }

      if (!director.surname) {
        errors.surname = 'Please enter your surname.';
      }
    }

    if (isCreditVettingEnabled) {
      if (!director.title) {
        errors.title = 'Please select a title.';
      }

      if (!director.initials) {
        errors.initials = 'Please enter your initials.';
      }

      if (director.identityType?.value !== IdentityTypeDirectors.RSA_ID && !director.gender) {
        errors.gender = 'Gender is invalid.';
      }
    }

    return errors;
  }

  function getCleanedValues(values: FormState): FormState {
    return {
      ...values,
      directors: values.directors.map((director) => {
        return {
          ...director,
          idNumber: stripWhitespace(director.idNumber),
        };
      }),
    };
  }

  function isAnyErrors(errors: FormikErrors<FormState>) {
    let errorsExist = false;

    Object.keys(errors).forEach((key) => {
      if (key !== 'directors' && errors[key] !== null) {
        errorsExist = true;
      }
    });

    // @ts-ignore
    Object.keys(errors.directors).forEach((key) => {
      if (errors[key] !== null) {
        errorsExist = true;
      }
    });

    return errorsExist;
  }

  function getErrors(values: FormState) {
    const errors: FormikErrors<FormState> = {
      directors: [],
    };

    if (isPassportEnabled) {
      values.directors.forEach((director, index) => {
        const directorErrors = validateDirectorPassportEnabled(director);

        if (Object.keys(directorErrors).length > 0) {
          // @ts-ignore
          errors.directors[index] = directorErrors;
        }
      });

      if (isCreditVettingEnabled && !values.creditCheckConsent) {
        errors.creditCheckConsent = 'Credit check consent not granted';
      }
    } else if (isCreditVettingEnabled) {
      if (!values.creditCheckConsent) {
        errors.creditCheckConsent = 'Credit check consent not granted';
      }

      errors.directors = validateDirectorsCreditVettingEnabled(values.directors);
    } else {
      values.directors.forEach((director, index) => {
        const directorErrors = validateDirectorCreditVettingDisabled(director);

        if (Object.keys(directorErrors).length > 0) {
          // @ts-ignore
          errors.directors[index] = directorErrors;
        }
      });
    }

    return errors;
  }

  function validateForm(values: FormState) {
    const valuesClean = getCleanedValues(values);
    const errors = getErrors(valuesClean);
    const anyErrors = isAnyErrors(errors);

    if (!anyErrors) {
      setFormValues(valuesClean);
      props.toggleNext(true);
    } else {
      props.toggleNext(false);
    }

    return errors;
  }
}
